import React from 'react';
import { Check, Shield, Heart } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getGenericWhatsAppLink } from '../../utils/whatsapp';

// Official WhatsApp SVG Icon (chat bubble with phone)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Hero = () => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  
  const whatsappLink = getGenericWhatsAppLink(language);

  const trustBullets = [
    { icon: WhatsAppIcon, text: t('hero.features.french') },
    { icon: Shield, text: t('hero.features.selected') },
    { icon: Heart, text: t('hero.features.easy') },
  ];

  // Cloudinary video URL for background
  const backgroundVideoUrl = 'https://res.cloudinary.com/dzx5jx8fr/video/upload/v1767636094/videobackground.mp4_2_s0sfie.mp4';

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video Background - Extends behind header, covers entire hero */}
      <div className="absolute inset-0 -top-20 z-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute top-1/2 left-1/2 min-w-full min-h-full w-auto h-auto -translate-x-1/2 -translate-y-1/2 object-cover"
        >
          <source src={backgroundVideoUrl} type="video/mp4" />
        </video>
        {/* Minimal overlay for text readability - no white veil */}
        <div className="absolute inset-0 bg-black/20" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 pt-28">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-md border border-white/50 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-sm text-slate-700 font-medium">{t('hero.badge')}</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100 drop-shadow-lg">
            {t('hero.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-300">
              {t('hero.titleHighlight')}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-white/95 font-medium mb-6 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200 drop-shadow-md">
            {t('hero.subtitle')}
          </p>

          {/* Reassurance text */}
          <p className="text-base md:text-lg text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300 drop-shadow">
            {t('hero.description')}
          </p>

          {/* Trust Bullets */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 mb-10 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-400">
            {trustBullets.map((bullet, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full shadow-sm border border-white/50 hover:shadow-md transition-all duration-300"
              >
                <bullet.icon className="w-4 h-4 text-teal-500" />
                <span className="text-sm text-slate-700 font-medium">{bullet.text}</span>
              </div>
            ))}
          </div>

          {/* Primary CTA */}
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 delay-500">
            <Button
              asChild
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-emerald-500/30 transition-all duration-300 hover:shadow-emerald-500/40 hover:scale-105 group"
            >
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon className="w-5 h-5 mr-2 group-hover:animate-bounce" />
                {t('hero.cta')}
              </a>
            </Button>
            <p className="text-sm text-white/70 mt-4 drop-shadow">{t('hero.ctaSubtext')}</p>
          </div>
        </div>
      </div>

      {/* Wave decoration at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
